// src/components/resources/tools/generators/ReviewMockup/ReviewMockupGenerator.tsx

'use client'

import { useCallback, useMemo, useRef, useState, type ChangeEvent } from 'react'
import Link from 'next/link'
import { Upload } from 'lucide-react'
import { clsx } from 'clsx'
import { toast } from 'sonner'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import { captureAndDownload } from '../shared/exportImage'
import { ExportButton } from '../shared/ExportButton'
import { useExport } from '../shared/useExport'
import { waitForStable } from '../shared/waitForStable'
import { usePreviewDeferral, useStaleRef } from '../shared/usePreviewDeferral'
import { PostExportPrompt } from '../shared/PostExportPrompt'
import { usePostExportPrompt } from '../shared/usePostExportPrompt'
import { GeneratorExportBridge } from '../shared/GeneratorExportBridge'
import { trackGeneratorBridgeClick } from '../shared/trackGeneratorBridge'
import {
  generatorHasExportHint,
  generatorHasPostExportFunnel,
  resolveReviewPostExportVariants,
} from '~/lib/resources/generator-growth-config'
import { MAX_UPLOAD_BYTES } from '../shared/uploadLimits'
import { GoogleReview } from './GoogleReview'
import { AppStoreReview } from './AppStoreReview'
import { YelpReview } from './YelpReview'
import {
  DEFAULT_CONFIG,
  type AppStoreConfig,
  type AppStoreReviewItem,
  type Rating,
  type ReviewConfig,
  type ReviewPlatform,
} from './types'

// Preserve the module's pre-decomposition public surface: these were exported
// from this file before the types moved to ./types, so re-export them here so
// any external `import { ReviewConfig, DEFAULT_CONFIG } from './ReviewMockupGenerator'`
// keeps working (no behavior change).
export {
  DEFAULT_CONFIG,
  type AppStoreConfig,
  type AppStoreReviewItem,
  type Rating,
  type ReviewConfig,
  type ReviewPlatform,
} from './types'

/**
 * Props accepted by {@link ReviewMockupGenerator}. All optional so the
 * umbrella route (no preset) and the per-platform variant routes (Google
 * Business / App Store / Yelp) share one component.
 */
interface ReviewMockupGeneratorProps {
  /**
   * Review platform to boot into. Variant routes pass this so the page renders
   * in the right card styling on first paint; the umbrella route omits it and
   * defaults to Google.
   */
  initialPlatform?: ReviewPlatform
  /**
   * Analytics slug override so variant routes attribute engagement / download
   * events to their own slug instead of the umbrella default.
   */
  trackingSlug?: string
}

/**
 * ReviewMockupGenerator
 *
 * A React component that provides a UI for generating and previewing mock reviews
 * for different platforms (Google, App Store, Yelp). Allows configuration of review
 * details and avatar upload.
 */
export function ReviewMockupGenerator({
  initialPlatform,
  trackingSlug,
}: ReviewMockupGeneratorProps = {}): JSX.Element {
  // Default to the umbrella's live registered slug. 'review-mockup-generator'
  // is a dead slug that 301-redirects to 'review-testimonial-generator', so
  // attributing analytics to it would misbucket every umbrella-page event.
  const trackingResourceSlug = trackingSlug ?? 'review-testimonial-generator'
  // State for the review configuration
  const [config, setConfig] = useState<ReviewConfig>(() => ({
    ...DEFAULT_CONFIG,
    ...(initialPlatform ? { platform: initialPlatform } : {}),
  }))

  // Render the review-card preview from a `useDeferredValue` snapshot so typing
  // in the controls commits immediately while the heavy card re-renders at a
  // lower priority. Export waits for the snapshot to converge so the PNG matches
  // the live controls.
  const { deferred: deferredConfig, isStale } = usePreviewDeferral(config)
  const previewStaleRef = useStaleRef(isStale)

  // Shared export lifecycle: warm html2canvas on mount, re-entrancy guard,
  // and the `isExporting` flag for the Download affordance.
  const { isExporting, runExport } = useExport()

  // Ref to the styled preview container, captured by the PNG export.
  const previewRef = useRef<HTMLDivElement>(null)

  // Initialize engagement timers - memoized for stable references and optimal performance
  const [getTotalEngagementMs] = useMemo(
    () => createEngagementTimers(),
    []
  )
  const { open: postExportOpen, onOpenChange: onPostExportOpenChange, promptAfterExport } =
    usePostExportPrompt(trackingResourceSlug)
  const showGrowthFunnel = generatorHasPostExportFunnel(trackingResourceSlug)
  const showExportHint = generatorHasExportHint(trackingResourceSlug)
  const postExportVariantLinks = useMemo(
    () => resolveReviewPostExportVariants(config.platform, trackingResourceSlug),
    [config.platform, trackingResourceSlug],
  )

  /**
   * Captures the styled review preview as a retina-density PNG and
   * triggers a browser download. Mirrors
   * SocialMediaMockupGenerator.handleExport (dynamic html2canvas import →
   * canvas.toBlob → <a download>.png) with success/failure analytics.
   */
  const handleExport = useCallback((): Promise<void> => runExport(async beginCapture => {
    if (!previewRef.current) return
    // Wait for the deferred preview snapshot to converge so the PNG matches the
    // live controls; fail loudly if it can't settle rather than export stale.
    // Flip the loading state only AFTER the gate so it tracks the capture.
    const stabilized = await waitForStable(() => !previewStaleRef.current)
    if (!stabilized) {
      toast.error('Preview is still updating — pause a moment, then export again.')
      return
    }
    beginCapture()
    await captureAndDownload({
      element: previewRef.current,
      format: 'png',
      filename: `review-mockup-${config.platform}-${Date.now()}`,
      trackingSlug: trackingResourceSlug,
      engagementMs: getTotalEngagementMs(),
      onSuccess: () => {
        toast.success('Review mockup exported')
        if (showGrowthFunnel) promptAfterExport()
      },
    })
  }), [runExport, config.platform, getTotalEngagementMs, trackingResourceSlug, previewStaleRef, promptAfterExport, showGrowthFunnel])

  /**
   * Handles avatar image upload and updates the config with the image as a data URL.
   * @param {ChangeEvent<HTMLInputElement>} event - The input change event from the file input.
   */
  const handleAvatarUpload = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const input = event.target
      const file: File | undefined = input.files?.[0]
      if (!file) return
      if (file.size > MAX_UPLOAD_BYTES) {
        toast.error('Image is too large (max 10 MB). Please choose a smaller file.')
        input.value = ''
        return
      }

      const reader = new FileReader()
      reader.onerror = (): void => {
        toast.error('Could not read that image. Please try another file.')
        input.value = ''
      }
      reader.onload = (e: ProgressEvent<FileReader>): void => {
        const result = e.target?.result
        if (typeof result === 'string') {
          setConfig((prev: ReviewConfig) => ({ ...prev, avatar: result }))

          // Track avatar upload - user uploaded reviewer avatar image
          sendResourceEvent('interact', {
            resourceType: 'tool',
            resourceSlug: trackingResourceSlug,
            engagementMs: getTotalEngagementMs(),
            params: {
              tool_type: 'generator',
              interaction_type: 'avatar_upload',
              file_type: file.type,
              file_size: file.size
            }
          })
        }
      }
      reader.readAsDataURL(file)
    },
    [getTotalEngagementMs, trackingResourceSlug]
  )

  /** Patch a field on the namespaced App Store config. */
  const updateAppStore = useCallback(
    (patch: Partial<AppStoreConfig>): void => {
      setConfig(prev => ({ ...prev, appStore: { ...prev.appStore, ...patch } }))
    },
    [],
  )

  /** Patch a single field on one of the App Store additional reviews. */
  const updateAppStoreReview = useCallback(
    (index: number, patch: Partial<AppStoreReviewItem>): void => {
      setConfig(prev => ({
        ...prev,
        appStore: {
          ...prev.appStore,
          additionalReviews: prev.appStore.additionalReviews.map((r, i) =>
            i === index ? { ...r, ...patch } : r,
          ),
        },
      }))
    },
    [],
  )

  /** App-icon upload handler (data-URL, client-side only). */
  const handleIconUpload = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const input = event.target
      const file = input.files?.[0]
      if (!file) return
      if (file.size > MAX_UPLOAD_BYTES) {
        toast.error('Image is too large (max 10 MB). Please choose a smaller file.')
        input.value = ''
        return
      }
      const reader = new FileReader()
      reader.onerror = (): void => {
        toast.error('Could not read that image. Please try another file.')
        input.value = ''
      }
      reader.onload = (e: ProgressEvent<FileReader>): void => {
        const result = e.target?.result
        if (typeof result === 'string') updateAppStore({ icon: result })
      }
      reader.readAsDataURL(file)
    },
    [updateAppStore],
  )

  /**
   * Available rating options for the review.
   */
  const ratingOptions: Rating[] = useMemo<Rating[]>(
    () => [1, 2, 3, 4, 5],
    []
  )

  /**
   * Memoized preview component based on the selected platform.
   */
  const preview: JSX.Element = useMemo((): JSX.Element => {
    switch (deferredConfig.platform) {
      case 'google':
        return <GoogleReview config={deferredConfig} />
      case 'appstore':
        return <AppStoreReview config={deferredConfig} />
      case 'yelp':
      default:
        return <YelpReview config={deferredConfig} />
    }
  }, [deferredConfig])

  return (
    <div className="flex w-full flex-col gap-10 lg:flex-row">
      <aside className="w-full space-y-6 rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-xl backdrop-blur dark:border-slate-700 dark:bg-slate-900/70 lg:w-[360px]">
        <section aria-labelledby="platform" className="space-y-3">
          <h2 id="platform" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Platform
          </h2>
          <div className="grid grid-cols-3 gap-2 text-sm">
            {(['google', 'appstore', 'yelp'] as ReviewPlatform[]).map(platform => (
              <button
                key={platform}
                type="button"
                onClick={() => setConfig(prev => ({ ...prev, platform }))}
                className={clsx(
                  'rounded-xl border px-3 py-2 font-medium capitalize transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
                  config.platform === platform
                    ? 'border-transparent bg-slate-900 text-white shadow-lg dark:bg-sky-500'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
                )}
                aria-pressed={config.platform === platform}
              >
                {platform}
              </button>
            ))}
          </div>
          {trackingResourceSlug === 'review-testimonial-generator' && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Platform-specific entry points:{' '}
              <Link
                href="/resources/tools/generators/google-business-review-generator"
                onClick={() =>
                  trackGeneratorBridgeClick({
                    resourceSlug: trackingResourceSlug,
                    placement: 'in_tool_variant',
                    targetType: 'variant_page',
                    destinationPath: '/resources/tools/generators/google-business-review-generator',
                    engagementMs: getTotalEngagementMs(),
                  })
                }
                className="font-medium text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
              >
                Google
              </Link>
              {' · '}
              <Link
                href="/resources/tools/generators/app-store-review-generator"
                onClick={() =>
                  trackGeneratorBridgeClick({
                    resourceSlug: trackingResourceSlug,
                    placement: 'in_tool_variant',
                    targetType: 'variant_page',
                    destinationPath: '/resources/tools/generators/app-store-review-generator',
                    engagementMs: getTotalEngagementMs(),
                  })
                }
                className="font-medium text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
              >
                App Store
              </Link>
              {' · '}
              <Link
                href="/resources/tools/generators/yelp-review-generator"
                onClick={() =>
                  trackGeneratorBridgeClick({
                    resourceSlug: trackingResourceSlug,
                    placement: 'in_tool_variant',
                    targetType: 'variant_page',
                    destinationPath: '/resources/tools/generators/yelp-review-generator',
                    engagementMs: getTotalEngagementMs(),
                  })
                }
                className="font-medium text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
              >
                Yelp
              </Link>
            </p>
          )}
        </section>

        <section aria-labelledby="identity" className="space-y-3">
          <h2 id="identity" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Reviewer details
          </h2>
          <div className="space-y-3 text-sm">
            <label className="flex flex-col gap-1">
              <span className="font-medium text-slate-700 dark:text-slate-200">Business name</span>
              <input
                type="text"
                value={config.businessName}
                onChange={event => setConfig(prev => ({ ...prev, businessName: event.target.value }))}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-medium text-slate-700 dark:text-slate-200">Reviewer name</span>
              <input
                type="text"
                value={config.reviewerName}
                onChange={event => setConfig(prev => ({ ...prev, reviewerName: event.target.value }))}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </label>
            {config.platform === 'yelp' && (
              <label className="flex flex-col gap-1">
                <span className="font-medium text-slate-700 dark:text-slate-200">Location</span>
                <input
                  type="text"
                  value={config.location}
                  onChange={event => setConfig(prev => ({ ...prev, location: event.target.value }))}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              </label>
            )}
            <label className="flex flex-col gap-1">
              <span className="font-medium text-slate-700 dark:text-slate-200">Date</span>
              <input
                type="text"
                value={config.date}
                onChange={event => setConfig(prev => ({ ...prev, date: event.target.value }))}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </label>
          </div>
        </section>

        <section aria-labelledby="review-content" className="space-y-3">
          <h2 id="review-content" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Review content
          </h2>
          <div className="space-y-3 text-sm">
            <label className="flex flex-col gap-1">
              <span className="font-medium text-slate-700 dark:text-slate-200">Headline</span>
              <input
                type="text"
                value={config.reviewTitle}
                onChange={event => setConfig(prev => ({ ...prev, reviewTitle: event.target.value }))}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-medium text-slate-700 dark:text-slate-200">Review text</span>
              <textarea
                value={config.reviewText}
                onChange={event => setConfig(prev => ({ ...prev, reviewText: event.target.value }))}
                className="min-h-[120px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm leading-relaxed text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </label>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={config.highlight}
                  onChange={event => setConfig(prev => ({ ...prev, highlight: event.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-sky-500 dark:focus:ring-sky-500"
                />
                Spotlight glow
              </label>
              {config.platform === 'yelp' && (
                <label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                  <input
                    type="checkbox"
                    checked={config.includeBadge}
                    onChange={event => setConfig(prev => ({ ...prev, includeBadge: event.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-sky-500 dark:focus:ring-sky-500"
                  />
                  Elite badge
                </label>
              )}
            </div>
            <label className="flex flex-col gap-1">
              <span className="font-medium text-slate-700 dark:text-slate-200">Rating</span>
              <select
                value={config.rating}
                onChange={event => setConfig(prev => ({ ...prev, rating: Number.parseInt(event.target.value, 10) as Rating }))}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              >
                {ratingOptions.map(value => (
                  <option key={value} value={value}>
                    {value} star{value > 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </section>

        {config.platform === 'appstore' && (
          <section aria-labelledby="appstore" className="space-y-3">
            <h2 id="appstore" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              App Store product page
            </h2>
            <div className="space-y-3 text-sm">
              <label className="flex flex-col gap-1">
                <span className="font-medium text-slate-700 dark:text-slate-200">App name</span>
                <input
                  type="text"
                  value={config.appStore.appName}
                  onChange={event => updateAppStore({ appName: event.target.value })}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-medium text-slate-700 dark:text-slate-200">Tagline</span>
                <input
                  type="text"
                  value={config.appStore.tagline}
                  onChange={event => updateAppStore({ tagline: event.target.value })}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              </label>
              <label className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-400 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                <Upload className="h-4 w-4" />
                Upload app icon
                <input type="file" accept="image/*" onChange={handleIconUpload} className="sr-only" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="font-medium text-slate-700 dark:text-slate-200">Avg rating</span>
                  <input
                    type="number"
                    min={0}
                    max={5}
                    step={0.1}
                    value={config.appStore.ratingAverage}
                    onChange={event =>
                      updateAppStore({
                        ratingAverage: Math.min(5, Math.max(0, Number.parseFloat(event.target.value) || 0)),
                      })
                    }
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="font-medium text-slate-700 dark:text-slate-200">Ratings count</span>
                  <input
                    type="text"
                    value={config.appStore.ratingCount}
                    onChange={event => updateAppStore({ ratingCount: event.target.value })}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                  />
                </label>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-medium text-slate-700 dark:text-slate-200">Star distribution (5★ → 1★)</span>
                <div className="grid grid-cols-5 gap-2">
                  {config.appStore.ratingDistribution.map((weight, i) => (
                    <input
                      key={i}
                      type="number"
                      min={0}
                      aria-label={`${5 - i} star weight`}
                      value={weight}
                      onChange={event => {
                        const next = [...config.appStore.ratingDistribution] as [
                          number, number, number, number, number,
                        ]
                        next[i] = Math.max(0, Number.parseFloat(event.target.value) || 0)
                        updateAppStore({ ratingDistribution: next })
                      }}
                      className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-center text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="font-medium text-slate-700 dark:text-slate-200">Age</span>
                  <input
                    type="text"
                    value={config.appStore.ageRating}
                    onChange={event => updateAppStore({ ageRating: event.target.value })}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="font-medium text-slate-700 dark:text-slate-200">Chart #</span>
                  <input
                    type="text"
                    value={config.appStore.chartRank}
                    onChange={event => updateAppStore({ chartRank: event.target.value })}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="font-medium text-slate-700 dark:text-slate-200">Category</span>
                  <input
                    type="text"
                    value={config.appStore.chartCategory}
                    onChange={event => updateAppStore({ chartCategory: event.target.value })}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                  />
                </label>
              </div>
              <div className="space-y-2">
                <span className="font-medium text-slate-700 dark:text-slate-200">Additional reviews</span>
                {config.appStore.additionalReviews.map((review, i) => (
                  <div key={i} className="space-y-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                    <input
                      type="text"
                      aria-label={`Additional review ${i + 1} title`}
                      value={review.title}
                      onChange={event => updateAppStoreReview(i, { title: event.target.value })}
                      placeholder="Review title"
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />
                    <textarea
                      aria-label={`Additional review ${i + 1} body`}
                      value={review.body}
                      onChange={event => updateAppStoreReview(i, { body: event.target.value })}
                      placeholder="Review text"
                      className="min-h-[60px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        type="text"
                        aria-label={`Additional review ${i + 1} reviewer`}
                        value={review.reviewerName}
                        onChange={event => updateAppStoreReview(i, { reviewerName: event.target.value })}
                        placeholder="Reviewer"
                        className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                      <input
                        type="text"
                        aria-label={`Additional review ${i + 1} date`}
                        value={review.date}
                        onChange={event => updateAppStoreReview(i, { date: event.target.value })}
                        placeholder="1w ago"
                        className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                      <select
                        aria-label={`Additional review ${i + 1} rating`}
                        value={review.rating}
                        onChange={event =>
                          updateAppStoreReview(i, {
                            rating: Number.parseInt(event.target.value, 10) as Rating,
                          })
                        }
                        className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      >
                        {ratingOptions.map(value => (
                          <option key={value} value={value}>
                            {value}★
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section aria-labelledby="media" className="space-y-3">
          <h2 id="media" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Avatar
          </h2>
          <label className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-400 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
            <Upload className="h-4 w-4" />
            Upload avatar
            <input type="file" accept="image/*" onChange={handleAvatarUpload} className="sr-only" />
          </label>
        </section>

        <section aria-labelledby="export" className="space-y-3">
          <h2 id="export" className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Export
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Download a retina-density PNG of your review mockup, ready for landing pages, decks, and social proof. No watermarks.
          </p>
          <ExportButton
            isExporting={isExporting}
            onExport={() => void handleExport()}
            icon={null}
            className="w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-sky-500 dark:hover:bg-sky-400"
          />
          {showExportHint && (
            <GeneratorExportBridge resourceSlug={trackingResourceSlug} getEngagementMs={getTotalEngagementMs} />
          )}
        </section>
      </aside>

      <div className="flex flex-1 items-center justify-center rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-950 to-black p-10">
        {/* The capture target (previewRef) is this OUTER wrapper, not the card
            itself, so html2canvas includes the spotlight-glow halo — which
            paints outside the card's border box — inside the exported image.
            When glow is on we give the wrapper matching dark padding + a solid
            background so the halo reads against a frame instead of being
            clipped to the card edge; when off, the wrapper is a zero-padding
            passthrough so the export stays a tight transparent crop of the
            card (unchanged from before). */}
        <div
          ref={previewRef}
          className={clsx(
            deferredConfig.highlight && 'rounded-2xl bg-slate-950 p-10'
          )}
        >
          <div
            className={clsx(
              'transition-shadow',
              // Spotlight glow — a soft elevated halo around the card.
              deferredConfig.highlight && 'rounded-xl shadow-[0_0_60px_-5px_rgba(255,255,255,0.35)]'
            )}
          >
            {preview}
          </div>
        </div>
      </div>

      {showGrowthFunnel && (
        <PostExportPrompt
          open={postExportOpen}
          onOpenChange={onPostExportOpenChange}
          resourceSlug={trackingResourceSlug}
          getEngagementMs={getTotalEngagementMs}
          variantLinks={postExportVariantLinks}
        />
      )}
    </div>
  )
}
